'use client';

import React, { useState } from 'react';

import Image from 'next/image';
import Link from 'next/link';
import { usePathname } from 'next/navigation';

import Icon from '@repo/ui/icon';
import {
  NavigationMenu,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
} from '@repo/ui/navigation-menu';
import { Sheet, SheetContent, SheetTrigger } from '@repo/ui/sheet';

import { toClassName } from 'helpers/format';

import LanguageSection from '../language-section';

import { styles } from '.';

type TProps = {
  locale: string;
  logo: {
    url: string;
    height: number;
    width: number;
    alternativeText: string;
  };
  socials: { icon: any; link: any }[];
  routes: {
    id: string;
    label: string;
    url: string;
    visibility?: string;
  }[];
};

const SideMenuSection = (props: TProps) => {
  const { logo, locale, routes, socials } = props;

  const [isOpen, setIsOpen] = useState(false);

  const pathname = usePathname();

  return (
    <div className={styles.container}>
      <Sheet open={isOpen} onOpenChange={setIsOpen}>
        <SheetTrigger aria-label="hamburger">
          <Icon accessor="bars" className="hover:opacity-60 duration-300" />
        </SheetTrigger>
        <SheetContent
          side="left"
          className="w-[90vw] !p-0 bg-white dark:bg-black dark:text-white dark:!border-white/10"
        >
          <div className={styles.sheet.container}>
            <div className={styles.sheet.content}>
              <div
                className={styles.sheet.head}
                onClick={() => setIsOpen(false)}
              >
                <Link href={`/${locale}`}>
                  <Image
                    src={logo?.url}
                    width={170}
                    height={50}
                    alt="logo"
                    className={styles.image}
                  />
                </Link>
              </div>

              <div className={styles.sheet.body}>
                <NavigationMenu className="!items-start !justify-start">
                  <NavigationMenuList className={styles.sheet.list}>
                    {routes.map((route) => {
                      if (route.visibility === 'HIDDEN') {
                        return;
                      }

                      if (route.id === 'contact' || route.id === 'apply') {
                        return;
                      }

                      const isHome =
                        pathname === `/${locale}` && route.url === `/${locale}`;

                      const isNotHome =
                        pathname !== `/${locale}` &&
                        route.url !== `/${locale}` &&
                        pathname.includes(route.url);

                      return (
                        <NavigationMenuItem
                          key={route.id}
                          onClick={() => setIsOpen(false)}
                          className="text-xl sm:text-xl hover:opacity-40 duration-300"
                        >
                          {route.url.includes('https') ||
                          route.url.includes('www') ? (
                            <a
                              href={route.url.replaceAll('*', '')}
                              target={route.url.includes('*') ? '_blank' : ''}
                              className={toClassName(
                                'font-medium uppercase',
                                pathname.includes(route.url) && '!opacity-20',
                              )}
                              rel="noreferrer"
                            >
                              {route.label}
                            </a>
                          ) : (
                            <Link
                              href={route.url.replaceAll('*', '')}
                              target={route.url.includes('*') ? '_blank' : ''}
                              passHref
                              as={route.url.replaceAll('*', '')}
                            >
                              <NavigationMenuLink
                                className={toClassName(
                                  'font-medium uppercase text-base',
                                  (isHome || isNotHome) && '!opacity-20',
                                )}
                              >
                                {route.label}
                              </NavigationMenuLink>
                            </Link>
                          )}
                        </NavigationMenuItem>
                      );
                    })}
                  </NavigationMenuList>
                </NavigationMenu>
              </div>

              <div className={styles.sheet.foot}>
                <LanguageSection locale={locale} />
              </div>
            </div>

            <NavigationMenu className="bg-black/5 dark:bg-white/10 w-full !max-w-full py-6 sm:py-12">
              <NavigationMenuList className="flex flex-col !items-start !px-8 gap-y-4">
                {routes.map((route) => {
                  if (
                    route.visibility === 'HIDDEN' ||
                    route.visibility === 'MENU_ONLY'
                  ) {
                    return;
                  }

                  if (route.id !== 'contact' && route.id !== 'apply') {
                    return;
                  }

                  return (
                    <NavigationMenuItem
                      key={route.id}
                      onClick={() => setIsOpen(false)}
                    >
                      <Link
                        href={route.url.replaceAll('*', '')}
                        target={route.url.includes('*') ? '_blank' : ''}
                        passHref
                        as={route.url.replaceAll('*', '')}
                      >
                        <NavigationMenuLink
                          className={toClassName(
                            styles.button,
                            // 'bg-green-300',
                            'whitespace-nowrap hover:opacity-20 uppercase !text-base',
                            pathname.includes(route.url) && '!opacity-20',
                          )}
                        >
                          {route.label}
                        </NavigationMenuLink>
                      </Link>
                    </NavigationMenuItem>
                  );
                })}
              </NavigationMenuList>
            </NavigationMenu>
            <div className="bg-black py-12 sm:py-16 px-12 !text-white">
              <ul className={styles.list}>
                {socials?.map((social, index: number) => (
                  <li key={`icon-${index}`}>
                    <a
                      href={social.link.url}
                      target={social.link.isExternal ? '_blank' : ''}
                      className={styles.icon}
                      aria-label={social.icon.accessor}
                      rel="noreferrer"
                    >
                      <Icon
                        type={social.icon.type}
                        accessor={social.icon.accessor}
                        className="text-xl sm:text-2xl"
                      />
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
};

export default SideMenuSection;
